import {
  PrinterOutlined,
  PlusOutlined,
  SearchOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { transportApi, type EligibleConsignmentDto, type GatePassSummary } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { usePageKeyboardShortcuts } from "../../shared/hooks/usePageKeyboardShortcuts";
import { useAuthStore } from "../../stores/authStore";
import { printGatePassStationery } from "../../shared/printing/transportPrintTemplates";
import { EntityAttachmentsPanel } from "../../shared/components/EntityAttachmentsPanel";
import { useOperationsWorkspace } from "../../shared/components/OperationsTransactionTabs";

const DEBOUNCE_MS = 350;

export function GatePassPage() {
  const workspace = useOperationsWorkspace();
  const [form] = Form.useForm();

  const [rows, setRows]             = useState<GatePassSummary[]>([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [page, setPage]             = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [tempKey, setTempKey]       = useState<string | null>(null);
  const [nextGatePassNo, setNextGatePassNo] = useState<string>("Loading…");
  const [gateTimePreview, setGateTimePreview] = useState(dayjs());

  // ── Consignment lookup state ───────────────────────────────────────────
  const [consignmentOptions, setConsignmentOptions] = useState<EligibleConsignmentDto[]>([]);
  const [consignmentLoading, setConsignmentLoading] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState<EligibleConsignmentDto | null>(null);

  // ── Branch state ───────────────────────────────────────────────────────
  const user         = useAuthStore(s => s.user);
  const authBranchId = useAuthStore(s => s.branchId ? Number(s.branchId) : undefined);
  const adminRoles   = ["admin", "administrator", "superadmin"];
  const isAdmin      = user?.roles.some(r => adminRoles.includes(r.toLowerCase())) ?? false;

  const [adminBranchOptions, setAdminBranchOptions] = useState<{ value: number; label: string }[]>([]);
  const [listBranchId, setListBranchId] = useState<number | undefined>(
    isAdmin ? undefined : authBranchId
  );
  const [formBranchId, setFormBranchId] = useState<number | undefined>(
    isAdmin ? undefined : authBranchId
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  usePageKeyboardShortcuts({
    "ctrl+n": () => openCreate(),
    "ctrl+s": () => void onSave(),
    "alt+w":  () => cancelDrawer(),
  });

  // ── List ───────────────────────────────────────────────────────────────

  const load = async (p = 1, branchOverride?: number) => {
    // Admin must select a branch before we can list — API requires branchId.
    const bid = isAdmin ? (branchOverride ?? listBranchId) : authBranchId;
    if (!bid) return;

    setLoading(true);
    try {
      const result = await transportApi.searchGatePasses("", p, 20, bid);
      setRows(result.data ?? []);
      setTotal(result.total ?? 0);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Failed to load gate passes"));
    } finally {
      setLoading(false);
    }
  };

  // Regular users auto-load; admins load once they pick a branch.
  useEffect(() => { if (workspace?.mode === "data") void load(1); }, [listBranchId, workspace?.mode]);

  // ── Admin branch lookup ────────────────────────────────────────────────

  useEffect(() => {
    if (!isAdmin) return;
    transportApi.searchBranchLookups("", 1, 200)
      .then(result => {
        setAdminBranchOptions(result.items.map(b => ({ value: b.id, label: b.label })));
      })
      .catch(() => { /* non-fatal: admin branch picker stays empty */ });
  }, [isAdmin]);

  // ── Drawer open/close ──────────────────────────────────────────────────

  const openCreate = () => {
    form.resetFields();
    const bid = isAdmin ? listBranchId : authBranchId;
    setFormBranchId(bid);
    setSelectedConsignment(null);
    setConsignmentOptions([]);
    // Generate a fresh UUID tempKey for pre-save attachment binding.
    setTempKey(crypto.randomUUID());
    setDrawerOpen(true);
    if (bid) void searchConsignments("", bid);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    form.resetFields();
    setSelectedConsignment(null);
    setTempKey(null);
  };

  const cancelDrawer = () => {
    if (tempKey) void transportApi.deleteAttachmentsByTempKey(tempKey);
    closeDrawer();
  };

  // ── Consignment search ─────────────────────────────────────────────────

  const searchConsignments = async (q: string, branchId?: number) => {
    const bid = branchId ?? formBranchId;
    if (!bid) return;
    setConsignmentLoading(true);
    try {
      const items = await transportApi.getEligibleConsignments(bid, q || undefined);
      setConsignmentOptions(items);
    } catch {
      setConsignmentOptions([]);
    } finally {
      setConsignmentLoading(false);
    }
  };

  const onConsignmentSearch = (q: string) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void searchConsignments(q), DEBOUNCE_MS);
  };

  const onConsignmentSelect = (id: number) => {
    setSelectedConsignment(consignmentOptions.find(c => c.id === id) ?? null);
  };

  const onBranchChange = (bid: number) => {
    setFormBranchId(bid);
    setSelectedConsignment(null);
    form.setFieldValue("consignmentId", undefined);
    setConsignmentOptions([]);
    void searchConsignments("", bid);
  };

  useEffect(() => {
    if (workspace?.mode === "entry") openCreate();
  }, [workspace?.mode]);

  useEffect(() => {
    if (!drawerOpen || !formBranchId) return;
    transportApi.getNextDocumentNumber("GatePass", formBranchId)
      .then((result) => setNextGatePassNo(result.nextNumber != null ? String(result.nextNumber) : (result.message ?? "No stationery assigned")))
      .catch(() => setNextGatePassNo("Unable to load next number"));
  }, [drawerOpen, formBranchId]);

  useEffect(() => {
    if (!drawerOpen) return;
    setGateTimePreview(dayjs());
    const timer = window.setInterval(() => setGateTimePreview(dayjs()), 1000);
    return () => window.clearInterval(timer);
  }, [drawerOpen]);

  // ── Save ───────────────────────────────────────────────────────────────

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const created = await transportApi.createGatePass({
        branchId:      isAdmin ? formBranchId : undefined,
        consignmentId: values.consignmentId,
        deliveredBy:   values.deliveredBy.trim(),
        deliveryDate: (values.deliveryDate as dayjs.Dayjs).toISOString(),
        remarks: values.remarks ?? undefined,
        tempKey,
      });
      const savedBranchId = formBranchId;
      message.success(`Gate Pass ${created.gatePassNo} created (Entry: ${dayjs(created.entryDate).format("DD/MM/YYYY HH:mm")})`);
      // Clear tempKey first — files are now bound. closeDrawer must not delete them.
      setTempKey(null);
      closeDrawer();
      if (isAdmin && savedBranchId) setListBranchId(savedBranchId);
      await load(1, savedBranchId);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Failed to create gate pass"));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 16 }}>
      <div className="operations-data-only" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Gate Pass</Typography.Title>
        <Space>
          {isAdmin && (
            <Select
              aria-label="List branch"
              placeholder="Select branch"
              value={listBranchId}
              onChange={branchId => {
                setPage(1);
                setListBranchId(branchId);
              }}
              options={adminBranchOptions}
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
            />
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Gate Pass</Button>
        </Space>
      </div>

      {isAdmin && !listBranchId && (
        <Alert
          className="operations-data-only"
          type="info"
          showIcon
          message="Select a branch to view its gate passes."
          style={{ marginBottom: 12 }}
        />
      )}

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={{ current: page, pageSize: 20, total, onChange: p => { setPage(p); void load(p); } }}
        columns={[
          { title: "Gate Pass No",  dataIndex: "gatePassNo", width: 130 },
          {
            title: "Entry Date",
            dataIndex: "entryDate",
            width: 150,
            render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm"),
          },
          {
            title: "Delivery Date",
            dataIndex: "deliveryDate",
            width: 120,
            render: (v?: string) => v ? dayjs(v).format("DD/MM/YYYY") : "—",
          },
          { title: "Consignment No", dataIndex: "consignmentNo", render: (v?: string) => v ?? "—" },
          { title: "Delivered By",   dataIndex: "deliveredBy",   render: (v?: string) => v ?? "—" },
          {
            title: "",
            width: 60,
            render: (_: unknown, row: GatePassSummary) => (
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={async () => {
                  try {
                    const report = await transportApi.getGatePassPrintReport(
                      row.id,
                      isAdmin ? listBranchId : undefined
                    );
                    printGatePassStationery(report);
                  } catch {
                    message.error("Print failed");
                  }
                }}
              />
            ),
          },
        ]}
      />

      <StaticDrawer
        title="New Gate Pass"
        width={640}
        open={drawerOpen}
        onClose={cancelDrawer}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <span className="kbd-hint">
              <kbd>Ctrl</kbd>+<kbd>S</kbd> Save &nbsp;·&nbsp; <kbd>Alt</kbd>+<kbd>W</kbd> Close
            </span>
            <Space>
              <Button onClick={cancelDrawer}>Cancel</Button>
              <SaveActionButton
                saving={saving}
                idleText="Create Gate Pass"
                savingText="Creating…"
                onClick={() => void onSave()}
              />
            </Space>
          </div>
        }
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={form} layout="vertical">

          {/* 1. Branch */}
          <Form.Item label="Branch">
            {isAdmin ? (
              <Select
                placeholder="Select branch"
                value={formBranchId}
                onChange={onBranchChange}
                options={adminBranchOptions}
                showSearch
                filterOption={(q, o) => (o?.label as string ?? "").toLowerCase().includes(q.toLowerCase())}
              />
            ) : (
              <Input
                readOnly
                prefix={<LockOutlined />}
                value={formBranchId ? `Branch ${formBranchId}` : "No branch assigned"}
              />
            )}
          </Form.Item>

          {/* 2. Gate Pass No — read-only, server-allocated */}
          <Form.Item
            label="Gate Pass No"
            extra="Allocated automatically from your branch stationery at save time."
          >
            <Input readOnly value={nextGatePassNo} />
          </Form.Item>

          {/* 3. Entry Date — read-only, server-set */}
          <Form.Item label="Entry Date / Gate Time" extra="The server records the final time when saved.">
            <Input readOnly value={gateTimePreview.format("DD/MM/YYYY HH:mm:ss")} />
          </Form.Item>

          {/* 4. Delivery Date */}
          <Form.Item
            name="deliveryDate"
            label="Delivery Date"
            rules={[{ required: true, message: "Enter the delivery date" }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>

          {/* 5. Consignment No */}
          <Form.Item
            name="consignmentId"
            label="Consignment No"
            rules={[{ required: true, message: "Select an arrived consignment" }]}
            extra="Only 'Arrived' consignments at this branch without an existing gate pass are shown."
          >
            <Select
              showSearch
              loading={consignmentLoading}
              placeholder={formBranchId ? "Type consignment no to search…" : "Select branch first"}
              filterOption={false}
              suffixIcon={<SearchOutlined />}
              onSearch={onConsignmentSearch}
              onSelect={(id: number) => onConsignmentSelect(id)}
              options={consignmentOptions.map(c => ({
                value: c.id,
                label: `${c.consignmentNo}${c.consigneeName ? ` — ${c.consigneeName}` : ""}`,
              }))}
              notFoundContent={consignmentLoading ? "Searching…" : "No eligible consignments"}
              disabled={!formBranchId}
            />
          </Form.Item>

          {/* Consignment receipt detail card */}
          {selectedConsignment && (
            <Descriptions
              size="small"
              bordered
              column={2}
              style={{ marginBottom: 16 }}
              title="Consignment Details"
            >
              <Descriptions.Item label="No" span={2}>
                {selectedConsignment.consignmentNo}
              </Descriptions.Item>
              <Descriptions.Item label="Consignor">
                {selectedConsignment.consignorName ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Consignee">
                {selectedConsignment.consigneeName ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="From">
                {selectedConsignment.origin ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="To">
                {selectedConsignment.destination ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Pkgs">
                {selectedConsignment.totalPackages}
              </Descriptions.Item>
              <Descriptions.Item label="Weight">
                {selectedConsignment.totalWeight} kg
              </Descriptions.Item>
              <Descriptions.Item label="Payment">
                <Tag>{selectedConsignment.paymentType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Booking">
                {dayjs(selectedConsignment.bookingDate).format("DD/MM/YYYY")}
              </Descriptions.Item>
            </Descriptions>
          )}

          {/* 6. Delivered By */}
          <Form.Item
            name="deliveredBy"
            label="Delivered By"
            rules={[{ required: true, message: "Enter recipient / delivery person name" }]}
          >
            <Input maxLength={200} placeholder="Name of person who received the consignment" />
          </Form.Item>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>

        </Form>

        {/* Pre-save attachments — bound server-side when the gate pass is created */}
        {tempKey && (
          <EntityAttachmentsPanel
            entityType="gate-pass"
            entityId={0}
            tempKey={tempKey}
            entityLabel="Gate Pass"
            branchId={formBranchId}
          />
        )}
      </StaticDrawer>
    </div>
  );
}
